import {
  type RecoveryCheckpoint,
  type RecoveryClock,
  type RecoveryConnectivity,
  type RecoveryIdentity,
  type RecoveryReceiptVerifier,
  type RecoveryResetContext,
  type RecoveryState,
  type SafeResetDecision,
  type SafeResetExecutor,
} from '@/types/recovery';
import { countUnaccountedPending, RecoveryGateService } from './RecoveryGateService';

const defaultClock: RecoveryClock = { now: () => new Date().toISOString() };
const noCheckpoint: RecoveryCheckpoint = async () => undefined;

interface SafeResetCoordinatorDependencies {
  gate: RecoveryGateService;
  connectivity: RecoveryConnectivity;
  executor: SafeResetExecutor;
  receiptVerifier: RecoveryReceiptVerifier;
  clock?: RecoveryClock;
  checkpoint?: RecoveryCheckpoint;
}

const RESET_PHASES = new Set([
  'validated',
  'reset_authorized',
  'pending_archived',
  'local_cleared',
  'canonical_pulled',
  'history_restored',
  'verifying',
]);

export class SafeResetCoordinator {
  private readonly gate: RecoveryGateService;
  private readonly connectivity: RecoveryConnectivity;
  private readonly executor: SafeResetExecutor;
  private readonly receiptVerifier: RecoveryReceiptVerifier;
  private readonly clock: RecoveryClock;
  private readonly checkpoint: RecoveryCheckpoint;

  constructor(dependencies: SafeResetCoordinatorDependencies) {
    this.gate = dependencies.gate;
    this.connectivity = dependencies.connectivity;
    this.executor = dependencies.executor;
    this.receiptVerifier = dependencies.receiptVerifier;
    this.clock = dependencies.clock ?? defaultClock;
    this.checkpoint = dependencies.checkpoint ?? noCheckpoint;
  }

  async evaluate(identity: RecoveryIdentity): Promise<SafeResetDecision> {
    const state = await this.gate.getState(identity);
    if (!state) return { allowed: false, reasons: ['recovery_state_missing'], state: null };
    if (state.phase === 'completed') return { allowed: true, reasons: [], state };

    const reasons: string[] = [];
    if (!RESET_PHASES.has(state.phase)) reasons.push('recovery_not_validated');
    if (!state.validatedReceipt) reasons.push('validated_receipt_missing');
    if (countUnaccountedPending(state) > 0) reasons.push('unaccounted_pending_items');
    if (!state.escrow?.manifestVerified) reasons.push('escrow_not_verified');

    if (state.validatedReceipt) {
      const { receipt, verification } = state.validatedReceipt;
      if (
        receipt.recoveryId !== state.recoveryId ||
        receipt.companyId !== identity.companyId ||
        receipt.userId !== identity.userId ||
        receipt.deviceId !== identity.deviceId ||
        receipt.manifestSha512 !== state.escrow?.prepared.manifestObject.ciphertextSha512
      ) {
        reasons.push('validated_receipt_mismatch');
      }
      if (receipt.expiresAt) {
        const expiresAt = Date.parse(receipt.expiresAt);
        const now = Date.parse(this.clock.now());
        if (Number.isNaN(expiresAt) || Number.isNaN(now)) {
          reasons.push('validated_receipt_expiration_invalid');
        } else if (expiresAt <= now) {
          reasons.push('validated_receipt_expired');
        }
      }
      if (!verification.valid) {
        reasons.push('validated_receipt_untrusted');
      } else if (state.escrow) {
        try {
          const currentVerification = await this.receiptVerifier.verify(receipt, {
            identity,
            recoveryId: state.recoveryId,
            manifestSha512: state.escrow.prepared.manifestObject.ciphertextSha512,
          });
          if (!currentVerification.valid) {
            reasons.push('validated_receipt_untrusted');
          }
        } catch {
          reasons.push('validated_receipt_unverifiable');
        }
      }
    }

    if (!(await this.connectivity.isOnline())) reasons.push('device_offline');
    return { allowed: reasons.length === 0, reasons, state };
  }

  async execute(identity: RecoveryIdentity): Promise<RecoveryState> {
    const decision = await this.evaluate(identity);
    if (!decision.allowed || !decision.state) {
      throw new Error(`Reset seguro bloqueado: ${decision.reasons.join(', ')}.`);
    }
    if (decision.state.phase === 'completed') return decision.state;

    const recoveryId = decision.state.recoveryId;
    try {
      let state = decision.state;
      let canonicalReadyVerified = false;
      let canonicalPulledThisRun = false;
      if (state.phase === 'validated') {
        state = await this.gate.transition(identity, recoveryId, 'reset_authorized');
        await this.emitCheckpoint('reset_authorized', state);
      }

      if (state.phase === 'reset_authorized') {
        await this.executor.verifyCanonicalReady(
          this.buildContext(state, 'verify-canonical-ready')
        );
        canonicalReadyVerified = true;
        await this.executor.archiveAccountedPending(this.buildContext(state, 'archive-pending'));
        state = await this.gate.transition(identity, recoveryId, 'pending_archived');
        await this.emitCheckpoint('pending_archived', state);
      }

      if (state.phase === 'pending_archived') {
        if (!canonicalReadyVerified) {
          await this.executor.verifyCanonicalReady(
            this.buildContext(state, 'verify-canonical-ready')
          );
        }
        await this.verifyCurrentReceipt(state);
        await this.executor.clearLocalData(this.buildContext(state, 'clear-local'));
        state = await this.gate.transition(identity, recoveryId, 'local_cleared');
        await this.emitCheckpoint('local_cleared', state);
      }

      if (state.phase === 'local_cleared') {
        // O checkpoint já está durável antes de reabrir o processo que fará o pull.
        await this.executor.ensureFreshRuntime?.();
        await this.executor.pullCanonicalState(this.buildContext(state, 'pull-canonical'));
        canonicalPulledThisRun = true;
        state = await this.gate.transition(identity, recoveryId, 'canonical_pulled');
        await this.emitCheckpoint('canonical_pulled', state);
      }

      if (state.phase === 'canonical_pulled') {
        if (!canonicalPulledThisRun) {
          await this.executor.pullCanonicalState(this.buildContext(state, 'pull-canonical'));
        }
        await this.executor.restoreHistoricalState(this.buildContext(state, 'restore-history'));
        state = await this.gate.transition(identity, recoveryId, 'history_restored');
        await this.emitCheckpoint('history_restored', state);
      }

      if (state.phase === 'history_restored') {
        state = await this.gate.transition(identity, recoveryId, 'verifying');
      }

      if (state.phase === 'verifying') {
        const verification = await this.executor.verifyRestoredState(
          this.buildContext(state, 'verify-restored')
        );
        if (!verification.valid || verification.unaccountedPendingCount !== 0) {
          throw new Error('Validacao final da recuperacao falhou.');
        }
        state = await this.gate.transition(identity, recoveryId, 'completed');
        await this.emitCheckpoint('recovery_verified', state);
      }

      return state;
    } catch (error) {
      await this.gate.recordError(identity, recoveryId, error);
      throw error;
    }
  }

  private buildContext(state: RecoveryState, stage: string): RecoveryResetContext {
    if (!state.validatedReceipt) throw new Error('Receipt validado ausente.');
    return {
      identity: state.identity,
      recoveryId: state.recoveryId,
      receipt: state.validatedReceipt,
      pendingItems: Object.values(state.pendingItems),
      idempotencyKey: `${state.recoveryId}:${stage}`,
    };
  }

  private async verifyCurrentReceipt(state: RecoveryState): Promise<void> {
    if (!state.validatedReceipt || !state.escrow) {
      throw new Error('Receipt validado ausente antes da limpeza local.');
    }
    const verification = await this.receiptVerifier.verify(
      state.validatedReceipt.receipt,
      {
        identity: state.identity,
        recoveryId: state.recoveryId,
        manifestSha512: state.escrow.prepared.manifestObject.ciphertextSha512,
      }
    );
    if (!verification.valid) {
      throw new Error('Receipt de recovery nao pode autorizar a limpeza local.');
    }
  }

  private async emitCheckpoint(
    name: Parameters<RecoveryCheckpoint>[0]['name'],
    state: RecoveryState
  ): Promise<void> {
    await this.checkpoint({
      name,
      identity: state.identity,
      recoveryId: state.recoveryId,
      phase: state.phase,
    });
  }
}
